import React, { useContext, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Card, CardContent, CardHeader, Chip, IconButton, Tab, Tabs, Tooltip, Typography } from "@material-ui/core";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

/**
 * Central da API: manual de uso da API pública, da API oficial, dos webhooks por canal e do n8n.
 * Textos em translate/languages/*.js (chave centralApi). Exemplos de código não são traduzidos.
 */
const useStyles = makeStyles((theme) => ({
  root: { flex: 1, padding: theme.spacing(3), paddingBottom: theme.spacing(6), overflowY: "auto", maxWidth: 1200, margin: "0 auto", ...theme.scrollbarStyles },
  title: { fontWeight: 700, fontSize: "1.75rem" },
  subtitle: { marginTop: theme.spacing(0.5), color: theme.palette.text.secondary },
  card: { borderRadius: 12, marginBottom: theme.spacing(3) },
  code: {
    position: "relative",
    backgroundColor: theme.palette.type === "dark" ? "rgba(0,0,0,0.35)" : "#f8f9fa",
    padding: theme.spacing(2), paddingRight: theme.spacing(6), borderRadius: 8,
    fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace", fontSize: "0.8rem", lineHeight: 1.6,
    whiteSpace: "pre-wrap", wordBreak: "break-word", margin: theme.spacing(1, 0, 2),
    border: `1px solid ${theme.palette.divider}`
  },
  copy: { position: "absolute", top: 4, right: 4 },
  table: { width: "100%", borderCollapse: "collapse", margin: theme.spacing(1, 0, 2), fontSize: "0.85rem",
    "& th, & td": { textAlign: "left", padding: theme.spacing(0.75, 1), borderBottom: `1px solid ${theme.palette.divider}`, verticalAlign: "top" },
    "& th": { fontWeight: 600 } },
  method: { fontWeight: 700, marginRight: theme.spacing(1) },
  p: { marginBottom: theme.spacing(1.5) }
}));

const t = (k) => i18n.t(`centralApi.${k}`);

const Codigo = ({ texto }) => {
  const classes = useStyles();
  const copiar = () => {
    navigator.clipboard?.writeText(texto).then(() => toast.success(t("copied")), () => toast.error(t("copyError")));
  };
  return (
    <pre className={classes.code}>
      <Tooltip title={t("copy")}>
        <IconButton size="small" className={classes.copy} onClick={copiar} aria-label={t("copy")}>
          <FileCopyOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <code>{texto}</code>
    </pre>
  );
};

const Campos = ({ linhas }) => {
  const classes = useStyles();
  return (
    <table className={classes.table}>
      <thead><tr><th scope="col">{t("field")}</th><th scope="col">{t("required")}</th><th scope="col">{t("description")}</th></tr></thead>
      <tbody>
        {linhas.map(([nome, obrig]) => (
          <tr key={nome}><td><code>{nome}</code></td><td>{obrig ? t("yes") : t("no")}</td><td>{t(`fields.${nome.replace(/[^A-Za-z0-9]/g, "_")}`)}</td></tr>
        ))}
      </tbody>
    </table>
  );
};

const Secao = ({ id, metodo, rota, children }) => {
  const classes = useStyles();
  return (
    <Card className={classes.card} variant="outlined" id={id}>
      <CardHeader
        title={<Typography component="h2" variant="h6">{t(`sections.${id}.title`)}</Typography>}
        subheader={rota ? <span><Chip size="small" label={metodo} className={classes.method} />{rota}</span> : null}
      />
      <CardContent>
        <Typography className={classes.p}>{t(`sections.${id}.text`)}</Typography>
        {children}
      </CardContent>
    </Card>
  );
};

const CentralApi = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [aba, setAba] = useState(0);
  const base = useMemo(() => String(process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, ""), []);
  const TOKEN = "SEU_TOKEN_DA_CONEXAO";
  const NUM = "5511999999999";

  const ex = {
    send: `curl -X POST "${base}/api/messages/send" \\
  -H "Authorization: Bearer ${TOKEN}" \\
  -H "Content-Type: application/json" \\
  -d '{"number":"${NUM}","body":"Olá! Seu pedido saiu para entrega.","queueId":1,"closeTicket":false}'`,
    sendMedia: `curl -X POST "${base}/api/messages/send" \\
  -H "Authorization: Bearer ${TOKEN}" \\
  -F number=${NUM} \\
  -F body="Segue o boleto" \\
  -F medias=@/caminho/boleto.pdf`,
    sendBase64: `curl -X POST "${base}/api/messages/send" \\
  -H "Authorization: Bearer ${TOKEN}" -H "Content-Type: application/json" \\
  -d '{"number":"${NUM}","body":"Segue seu boleto","mediaBase64":{"base64":"JVBERi0xLjQK...","fileName":"boleto.pdf"}}'`,
    sendButtons: `{
  "number": "${NUM}",
  "body": "Confirma o horário?",
  "buttons": [{ "id": "sim", "title": "Sim" }, { "id": "nao", "title": "Não" }]
}`,
    linkImage: `curl -X POST "${base}/api/messages/send/linkImage" \\
  -H "Authorization: Bearer ${TOKEN}" -H "Content-Type: application/json" \\
  -d '{"number":"${NUM}","url":"https://exemplo.com/foto.jpg","caption":"Nossa vitrine"}'`,
    check: `curl -X POST "${base}/api/messages/checkNumber" \\
  -H "Authorization: Bearer ${TOKEN}" -H "Content-Type: application/json" \\
  -d '{"number":"${NUM}"}'

# 200 {"existsInWhatsapp":true,"number":"${NUM}","numberFormatted":"${NUM}@s.whatsapp.net"}
# 400 {"existsInWhatsapp":false,...}`,
    sync: `curl -X POST "${base}/api/messages/external-sync" \\
  -H "Authorization: Bearer ${TOKEN}" -H "Content-Type: application/json" \\
  -d '{"number":"${NUM}","body":"Mensagem enviada pelo ERP","mediaType":"text","closeTicket":false}'`,
    list: `curl "${base}/api/messages/list/123" -H "Authorization: Bearer ${TOKEN}"`,
    oficial: `curl -X POST "${base}/api/oficial/send/text" \\
  -H "Authorization: Bearer ${TOKEN}" -H "Content-Type: application/json" \\
  -d '{"number":"${NUM}","body":"Olá pela API oficial"}'

curl -X POST "${base}/api/oficial/send/template" \\
  -H "Authorization: Bearer ${TOKEN}" -H "Content-Type: application/json" \\
  -d '{"number":"${NUM}","templateName":"confirmacao","language":"pt_BR","templateParams":["Maria","10/10"]}'

curl "${base}/api/oficial/check-window/${NUM}" -H "Authorization: Bearer ${TOKEN}"`,
    n8n: `Nó: HTTP Request
  Method: POST
  URL: ${base}/api/messages/send
  Authentication: Generic Credential Type > Header Auth
    Name:  Authorization
    Value: Bearer ${TOKEN}
  Send Body: JSON
    { "number": "{{ $json.telefone }}", "body": "{{ $json.mensagem }}" }
  Options > Retry On Fail: ligado (3 tentativas, 5000 ms)
  Options > Batching: 1 item a cada 3000 ms (evita bloqueio do número)`,
    erpBoleto: `curl -X POST "${base}/api/integracoes/erp/boleto" \\
  -H "Authorization: Bearer ${TOKEN}" -H "Content-Type: application/json" \\
  -d '{"whats":"${NUM}","cnpj":"12345678000190","codigoReceber":"123"}'

# 200 [{"valid":true}]   boleto enviado ao cliente
# 200 [{"valid":false}]  título não está em aberto no ERP (nada enviado)`,
    erpDanfe: `curl -X POST "${base}/api/integracoes/erp/danfe" \\
  -H "Authorization: Bearer ${TOKEN}" -H "Content-Type: application/json" \\
  -d '{"whats":"${NUM}","cnpj":"12345678000190","cliente_nota":"4321"}'`,
    n8nErp: `Fluxo: Webhook -> HTTP Request (login no ERP) -> HTTP Request (ERP devolve o PDF em base64) -> HTTP Request (envio)

Último nó: HTTP Request
  Method: POST
  URL: ${base}/api/messages/send
  Authentication: Header Auth (Authorization = Bearer ${TOKEN})
  Send Body: JSON
  {
    "number": "{{ $('Webhook').item.json.body.whats }}",
    "body": "Segue seu boleto",
    "closeTicket": true,
    "mediaBase64": { "base64": "{{ $json.data }}", "fileName": "boleto.pdf" }
  }

Não precisa do nó Convert to File: o PDF em base64 vai direto no JSON.`,
    erros: `401  ERR_SESSION_EXPIRED        cabeçalho Authorization ausente ou token vazio
403  Invalid token...           token não existe
403  ERR_FORBIDDEN              ticket ou conexão de outra empresa
404  Ticket nao encontrado       ticket não existe NA SUA empresa (o de outra empresa também dá 404)
400  Invalid number format...   number com letras
400  ERR_WAPP_NOT_INITIALIZED   conexão desconectada
400  ERR_CANAL_SEM_TRADUTOR     canal sem envio pela API
400  ERR_ENVIO_VAZIO            sem body e sem medias
400  ERR_SENDING_WAPP_MSG       a conexão não conseguiu enviar (ex.: imagem por link fora da conexão WhatsApp)
400  ERR_MEDIA_BASE64: ...      base64 inválido, arquivo executável, extensão proibida ou mais de 10 arquivos
413  ERR_MEDIA_BASE64: ...      arquivos somados acima de 16 MB
404  ERR_INTEGRACAO_SEM_CONFIG  integração com ERP não configurada para a sua empresa
429  ERR_INTEGRACAO_OCUPADA     muitos pedidos de boleto/nota ao mesmo tempo; tente de novo em instantes
502  ERR_INTEGRACAO_ERP         o ERP não respondeu ou recusou o login
503  ERR_INTEGRACAO_DESLIGADA   integração com ERP desligada neste servidor`
  };

  const abas = ["start", "messages", "oficial", "webhooks", "n8n", "errors"];

  return (
    <Box className={classes.root} component="main">
      <Typography component="h1" className={classes.title}>{t("title")}</Typography>
      <Typography className={classes.subtitle}>{t("subtitle")}</Typography>
      <Tabs value={aba} onChange={(e, v) => setAba(v)} variant="scrollable" indicatorColor="primary" textColor="primary" aria-label={t("title")} style={{ margin: "16px 0" }}>
        {abas.map((a) => <Tab key={a} label={t(`tabs.${a}`)} id={`aba-${a}`} aria-controls={`painel-${a}`} />)}
      </Tabs>

      <div role="tabpanel" id={`painel-${abas[aba]}`} aria-labelledby={`aba-${abas[aba]}`}>
        {aba === 0 && (<>
          <Secao id="intro"><Codigo texto={`${t("baseUrl")}: ${base}\n${t("header")}: Authorization: Bearer ${TOKEN}`} /></Secao>
          <Secao id="token" />
          <Secao id="security" />
          <Secao id="channels">
            <table className={classes.table}>
              <thead><tr><th scope="col">{t("channel")}</th><th scope="col">{t("text")}</th><th scope="col">{t("media")}</th><th scope="col">{t("notes")}</th></tr></thead>
              <tbody>
                {["whatsapp", "whatsapp01", "whatsapp02", "whatsapp03", "oficial"].map((c) => (
                  <tr key={c}><td>{t(`ch.${c}.name`)}</td><td>{t(`ch.${c}.text`)}</td><td>{t(`ch.${c}.media`)}</td><td>{t(`ch.${c}.notes`)}</td></tr>
                ))}
              </tbody>
            </table>
          </Secao>
        </>)}

        {aba === 1 && (<>
          <Secao id="send" metodo="POST" rota="/api/messages/send">
            <Campos linhas={[["number", true], ["body", true], ["medias", false], ["mediaBase64", false], ["queueId", false], ["userId", false], ["sendSignature", false], ["closeTicket", false], ["msdelay", false], ["quotedMsg", false], ["buttons", false], ["sections", false], ["buttonText", false], ["noRegister", false]]} />
            <Codigo texto={ex.send} />
            <Typography className={classes.p}>{t("sendMediaText")}</Typography>
            <Codigo texto={ex.sendMedia} />
            <Typography className={classes.p}>{t("sendBase64Text")}</Typography>
            <Codigo texto={ex.sendBase64} />
            <Typography className={classes.p}>{t("sendButtonsText")}</Typography>
            <Codigo texto={ex.sendButtons} />
          </Secao>
          <Secao id="linkImage" metodo="POST" rota="/api/messages/send/linkImage"><Campos linhas={[["number", true], ["url", true], ["caption", false]]} /><Codigo texto={ex.linkImage} /></Secao>
          <Secao id="checkNumber" metodo="POST" rota="/api/messages/checkNumber"><Codigo texto={ex.check} /></Secao>
          <Secao id="externalSync" metodo="POST" rota="/api/messages/external-sync">
            <Campos linhas={[["number", true], ["body", true], ["whatsappId", false], ["templateName", false], ["mediaType", false], ["mediaUrl", false], ["timestamp", false], ["queueId", false], ["closeTicket", false]]} />
            <Codigo texto={ex.sync} />
          </Secao>
          <Secao id="list" metodo="GET" rota="/api/messages/list/:ticketId"><Codigo texto={ex.list} /></Secao>
        </>)}

        {aba === 2 && (<>
          <Secao id="oficial">
            <table className={classes.table}>
              <tbody>
                {[["POST", "/api/oficial/send/text"], ["POST", "/api/oficial/send/image"], ["POST", "/api/oficial/send/audio"], ["POST", "/api/oficial/send/video"], ["POST", "/api/oficial/send/document"], ["POST", "/api/oficial/send/template"], ["POST", "/api/oficial/upload-media"], ["GET", "/api/oficial/media-limits"], ["GET", "/api/oficial/templates"], ["POST", "/api/oficial/validate-template"], ["GET", "/api/oficial/check-window/:number"], ["GET", "/api/oficial/postman-collection"]].map(([m, r]) => (
                  <tr key={r}><td><b>{m}</b></td><td><code>{r}</code></td><td>{t(`oficialRoutes.${r.split("/").filter(Boolean).slice(2).join("_").replace(/[^A-Za-z0-9_]/g, "")}`)}</td></tr>
                ))}
              </tbody>
            </table>
            <Codigo texto={ex.oficial} />
          </Secao>
          <Secao id="window" />
        </>)}

        {aba === 3 && (<><Secao id="webhooks" /><Secao id="webhookSecurity" /></>)}
        {aba === 4 && (<>
          <Secao id="erpNativo" metodo="POST" rota="/api/integracoes/erp/boleto">
            <Campos linhas={[["whats", true], ["cnpj", true], ["codigoReceber", true]]} />
            <Codigo texto={ex.erpBoleto} />
          </Secao>
          <Secao id="erpDanfe" metodo="POST" rota="/api/integracoes/erp/danfe">
            <Campos linhas={[["whats", true], ["cnpj", true], ["cliente_nota", true]]} />
            <Codigo texto={ex.erpDanfe} />
          </Secao>
          <Secao id="erpLimites" />
          <Secao id="n8n"><Codigo texto={ex.n8n} /></Secao><Secao id="n8nErp"><Codigo texto={ex.n8nErp} /></Secao><Secao id="n8nTips" />
        </>)}
        {aba === 5 && (<Secao id="errors"><Codigo texto={ex.erros} /></Secao>)}
        {user?.super && aba === 0 && <Secao id="admin" />}
      </div>
    </Box>
  );
};

export default CentralApi;
